import { Fragment, useMemo, useState } from 'react';
import { ArrowDownTrayIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useOpsPage } from '../hooks/useDailyInstallations';
import {
  BRAND, Note, PageHeader, States, Stat, monthName, num, selectClass, thisMonth,
} from '../components/OpsPageKit';

// Region filter. Applied here rather than on the server: the response already
// carries each network separately, so a region is just the networks it keeps,
// with the totals and busiest week re-added over them.
function forRegion(data, region) {
  if (!data || !region) return data;
  const networks = data.networks.filter((n) => n.key === region);
  const weekTotals = data.weeks.map((w) => networks.reduce((s, n) => s + (n.weeks[w.index] || 0), 0));
  const weekendTotals = data.weeks.map((w) => networks.reduce((s, n) => s + ((n.weekend_weeks || [])[w.index] || 0), 0));
  const weekend = weekendTotals.reduce((a, b) => a + b, 0);
  const done = data.weeks.filter((w) => w.status === 'complete');
  const best = done.length
    ? done.reduce((b, w) => (weekTotals[w.index] > weekTotals[b.index] ? w : b), done[0]) : null;
  return {
    ...data,
    networks,
    totals: {
      ...data.totals,
      completed: weekTotals.reduce((a, b) => a + b, 0),
      weeks: weekTotals,
      weekend_weeks: weekendTotals,
      weekend,
      weekdays: weekTotals.reduce((a, b) => a + b, 0) - weekend,
      areas: networks.reduce((c, n) => c + n.areas.length, 0),
      best_week: best ? { ...best, completed: weekTotals[best.index] } : null,
    },
  };
}

// Monthly Install Count - completed installs from the 1st of the month, by
// network (Region B / Region N), by area, by week.
//
// One table: each network is a collapsible group with its weekly subtotal, its
// areas underneath, and a grand total at the foot. Cells are shaded by volume so
// a quiet area or a dip week stands out without reading every number.

const shortDay = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const ukDate = (d) => { const [y, m, day] = String(d).split('-'); return `${day}/${m}/${y}`; };

// Shade a cell by its share of the busiest area-week, lightest to strongest.
function shade(value, max) {
  if (!value || !max) return undefined;
  const a = 0.06 + 0.34 * Math.min(value / max, 1);
  return { backgroundColor: `rgba(113, 28, 145, ${a.toFixed(3)})` };
}

function downloadCsv(data) {
  const head = ['Network', 'Area', ...data.weeks.map((w, i) => `Week ${i + 1} (${ukDate(w.from)}-${ukDate(w.to)})`), 'Weekdays (Mon-Fri)', 'Weekend (Sat-Sun)', 'Month total'];
  const lines = [head];
  data.networks.forEach((n) => {
    lines.push([`${n.label} (${n.network})`, 'All areas', ...n.weeks, n.weekdays ?? '', n.weekend ?? '', n.total]);
    n.areas.forEach((a) => lines.push([`${n.label} (${n.network})`, a.area, ...a.weeks, a.weekdays ?? '', a.weekend ?? '', a.total]));
  });
  lines.push([data.networks.length === 1 ? data.networks[0].label : 'All networks', 'Total', ...data.totals.weeks, data.totals.weekdays ?? '', data.totals.weekend ?? '', data.totals.completed]);
  const csv = lines.map((r) => r.map((c) => (/[",\n]/.test(String(c)) ? `"${String(c).replace(/"/g, '""')}"` : c)).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  const only = data.networks.length === 1 ? `-${data.networks[0].label.replace(/\s+/g, '-').toLowerCase()}` : '';
  a.download = `install-count-${data.month}${only}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function InstallCount() {
  const [month, setMonth] = useState(thisMonth());
  const [collapsed, setCollapsed] = useState(() => new Set());
  // '' = both, else a network key: BRSK (Region B) or NETOMNIA (Region N).
  const [region, setRegion] = useState('');

  const filters = useMemo(() => ({ month }), [month]);
  const {
    data: raw, isLoading, isFetching, refetch, isError,
  } = useOpsPage('install-count', filters);
  const data = useMemo(() => forRegion(raw, region), [raw, region]);

  const weeks = data?.weeks || [];
  const t = data?.totals || {};
  const c = data?.caveats || {};

  // Busiest single area-week, for shading.
  const maxCell = useMemo(() => Math.max(1, ...(data?.networks || [])
    .flatMap((n) => n.areas.flatMap((a) => a.weeks))), [data]);

  const toggle = (key) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const allCollapsed = (data?.networks || []).length > 0 && data.networks.every((n) => collapsed.has(n.key));

  // Each week's total, with its Saturday/Sunday installs underneath when
  // there were any ("3 wknd"), and the full split on hover.
  const weekCell = (w, value, strong = false, shadeIt = false, weekend = 0) => {
    if (w.status === 'future') return <td key={w.index} className="px-3 py-2 text-right text-gray-300">—</td>;
    return (
      <td
        key={w.index}
        className={`px-3 py-2 text-right tabular-nums ${strong ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
        style={shadeIt ? shade(value, maxCell) : undefined}
        title={value ? `${num(value - (weekend || 0))} weekdays · ${num(weekend || 0)} weekend` : undefined}
      >
        {num(value)}
        {weekend ? <div className="text-[10px] font-normal leading-tight text-amber-700">{num(weekend)} wknd</div> : null}
      </td>
    );
  };
  // Month-level weekday / weekend columns.
  const splitCells = (row, strong = false) => (
    <>
      <td className={`px-3 py-2 text-right tabular-nums ${strong ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{num(row.weekdays ?? 0)}</td>
      <td className={`px-3 py-2 text-right tabular-nums ${strong ? 'font-semibold' : ''} text-amber-700`}>{num(row.weekend ?? 0)}</td>
    </>
  );

  return (
    <div className="p-6">
      <PageHeader
        title="Monthly Install Count"
        subtitle="Completed installs from the start of the month, by network, area and week."
        month={month}
        onMonth={setMonth}
        onRefresh={refetch}
        fetching={isFetching}
      >
        <select value={region} onChange={(e) => setRegion(e.target.value)} className={selectClass} aria-label="Region">
          <option value="">Both regions</option>
          <option value="BRSK">Region B</option>
          <option value="NETOMNIA">Region N</option>
        </select>
        {data ? (
          <button
            type="button"
            onClick={() => downloadCsv(data)}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:border-brand"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            CSV
          </button>
        ) : null}
      </PageHeader>

      <States isLoading={isLoading} isError={isError} what="the install count" />

      {data && (
        <>
          {c.partial_month ? (
            <Note tone="red">
              <strong>{monthName(month)} is only partly covered.</strong> The installations mirror starts on{' '}
              {ukDate(c.data_from)}, so earlier weeks in this month are incomplete.
            </Note>
          ) : null}

          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Completed this month" value={num(t.completed)} sub={`${t.areas} areas`} accent />
            {/* Shares are always of BOTH regions (the unfiltered total), so a
                filtered view still says how big that region is overall. */}
            {region !== 'NETOMNIA' ? (
              <Stat label="Region B" value={num(t.by_network?.BRSK)} sub={raw.totals.completed ? `${Math.round((100 * (t.by_network?.BRSK || 0)) / raw.totals.completed)}% of all installs` : ''} />
            ) : null}
            {region !== 'BRSK' ? (
              <Stat label="Region N" value={num(t.by_network?.NETOMNIA)} sub={raw.totals.completed ? `${Math.round((100 * (t.by_network?.NETOMNIA || 0)) / raw.totals.completed)}% of all installs` : ''} />
            ) : null}
            <Stat
              label="Weekend installs"
              value={num(t.weekend)}
              sub={t.completed ? `${Math.round((100 * (t.weekend || 0)) / t.completed)}% of this month · ${num(t.weekdays)} on weekdays` : ''}
            />
            <Stat
              label="Busiest full week"
              value={t.best_week ? num(t.best_week.completed) : '—'}
              sub={t.best_week ? `${shortDay(t.best_week.from)} – ${shortDay(t.best_week.to)}` : 'no full week yet'}
            />
          </div>

          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
              <h2 className="text-sm font-semibold text-gray-700">Installs by area and week</h2>
              <button
                type="button"
                onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(data.networks.map((n) => n.key)))}
                className="text-xs font-medium hover:underline"
                style={{ color: BRAND }}
              >
                {allCollapsed ? 'Expand all' : 'Collapse all'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold uppercase tracking-wide">Network / area</th>
                    {weeks.map((w, i) => (
                      <th key={w.index} className={`px-3 py-2 text-right font-semibold ${w.status === 'future' ? 'text-gray-300' : ''}`}>
                        <div className="uppercase tracking-wide">Week {i + 1}</div>
                        <div className="font-normal normal-case">
                          {shortDay(w.from)}{w.from !== w.to ? `–${shortDay(w.to)}` : ''}
                        </div>
                        <div className="text-[10px] font-normal">
                          {w.status === 'in_progress' ? 'so far' : w.status === 'future' ? 'to come' : `wk ${w.iso_week}`}
                        </div>
                      </th>
                    ))}
                    <th className="border-l border-gray-200 px-3 py-2 text-right font-semibold uppercase tracking-wide">
                      Weekdays
                      <div className="text-[10px] font-normal normal-case">Mon–Fri</div>
                    </th>
                    <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide text-amber-700">
                      Weekend
                      <div className="text-[10px] font-normal normal-case">Sat–Sun</div>
                    </th>
                    <th className="px-3 py-2 text-right font-semibold uppercase tracking-wide">Month</th>
                  </tr>
                </thead>
                {data.networks.map((n) => {
                  const open = !collapsed.has(n.key);
                  return (
                    <tbody key={n.key} className="border-t border-gray-200">
                      <tr onClick={() => toggle(n.key)} className="cursor-pointer bg-brand-light/60 hover:bg-brand-light" aria-expanded={open}>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5 font-semibold text-gray-900">
                            {open ? <ChevronDownIcon className="h-4 w-4 text-gray-400" /> : <ChevronRightIcon className="h-4 w-4 text-gray-400" />}
                            {n.label}
                            <span className="text-xs font-normal text-gray-500">{n.network} · {n.areas.length} areas</span>
                          </div>
                        </td>
                        {weeks.map((w) => weekCell(w, n.weeks[w.index], true, false, (n.weekend_weeks || [])[w.index]))}
                        {splitCells(n, true)}
                        <td className="px-3 py-2 text-right font-bold tabular-nums" style={{ color: BRAND }}>{num(n.total)}</td>
                      </tr>
                      {open ? n.areas.map((a) => (
                        <tr key={a.area} className="border-t border-gray-50 hover:bg-gray-50/60">
                          <td className="py-1.5 pl-10 pr-3 text-gray-700">{a.area}</td>
                          {weeks.map((w) => weekCell(w, a.weeks[w.index], false, true, (a.weekend_weeks || [])[w.index]))}
                          {splitCells(a)}
                          <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-gray-900">{num(a.total)}</td>
                        </tr>
                      )) : null}
                    </tbody>
                  );
                })}
                <tfoot className="border-t-2 border-gray-300 bg-gray-50">
                  <tr>
                    <td className="px-3 py-2 font-bold text-gray-900">{region ? `${data.networks[0]?.label || ''} total` : 'All networks'}</td>
                    {weeks.map((w) => weekCell(w, t.weeks[w.index], true, false, (t.weekend_weeks || [])[w.index]))}
                    {splitCells(t, true)}
                    <td className="px-3 py-2 text-right text-base font-bold tabular-nums text-gray-900">{num(t.completed)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <Note tone="brand">
            <p>
              Counts <strong>completed installs</strong> by installation date — Installation Complete, with Mesh or TV,
              Dry Install and LV Installation; service calls and remediations aren&apos;t included. Weeks run Monday to
              Sunday and are cut at the start and end of the month, so the first and last week can be short. The
              current week shows installs so far. Weekends are included: each week&apos;s Saturday and Sunday installs
              show underneath its total (&ldquo;wknd&rdquo;), and the Weekdays / Weekend columns split the month.
            </p>
            {c.blank_contract_on_list ? (
              <p className="mt-1">
                {num(c.blank_contract_on_list)} {c.blank_contract_on_list === 1 ? 'install has' : 'installs have'} a blank
                {' '}Contract field in SharePoint. The network is taken from which job list each install is on, so they
                {' '}are still counted under the right region.
              </p>
            ) : null}
            {c.inferred_network ? (
              <p className="mt-1">
                {num(c.inferred_network)} {c.inferred_network === 1 ? 'install was' : 'installs were'} placed by area,
                {' '}as the job list recorded no network.
              </p>
            ) : null}
          </Note>
        </>
      )}
    </div>
  );
}

export default InstallCount;
